import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { workerFetch } from '../utils/apiUtils';
import { useScrollRestoration, ensureScrollable } from '../utils/scrollUtils';
import OnboardingTour from '../components/OnboardingTour';
import { useTour } from '../hooks/useTour';
import Header from '../components/Header';
import SkinProfile from '../components/SkinProfile';
import LoadingOverlay from '../components/LoadingOverlay';
import PWAInstallModal from '../components/PWAInstallModal';

// Import shared UTILITIES from SkinAnalysisPage
import {
    extractAnalysisFromError,
    extractScore,
    extractText,
    topLevelExplanationKeys,
    topLevelRecommendationKeySpace,
    topLevelDefinitionKeySpace,
    scoreKeySpace,
    scoreKeyUnderscore,
    scoreObjExplanationKey,
    nestedExplanationKeyFull,
    nestedDefinitionKeyFull,
    nestedRecommendationKeyFull,
    nestedDefinitionKeySpace,
    nestedExplanationKeySpace,
    nestedExplanationKey,
    nestedDefinitionKey,
    nestedRecommendationKey
} from '../constants/analysisConstants';

// Worker URL
const WORKER_URL = 'https://empty-rain-2654.er-hooman.workers.dev';

const HomePage = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fetchedData, setFetchedData] = useState(null);
    const [userProfile, setUserProfile] = useState(null);
    const [showInstallModal, setShowInstallModal] = useState(false);
    const navigate = useNavigate();
    const { startTour } = useTour();

    // Clear cache function for development
    const clearCache = async () => {
        if ('serviceWorker' in navigator) {
            const registrations = await navigator.serviceWorker.getRegistrations();
            for(let registration of registrations) {
                await registration.unregister();
                console.log('Service Worker unregistered');
            }
        }

        if ('caches' in window) {
            const cacheNames = await caches.keys();
            await Promise.all(
                cacheNames.map(cacheName => {
                    console.log('Deleting cache:', cacheName);
                    return caches.delete(cacheName);
                })
            );
            console.log('All caches cleared');
        }
        
        // Reload the page
        window.location.reload();
    };

    // Show install modal for mobile users
    useEffect(() => {
        const checkInstallPrompt = () => {
            // Check if user is on mobile and not already installed
            const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
            
            if (isMobile && !isStandalone) {
                // Show modal after 3 seconds
                setTimeout(() => {
                    setShowInstallModal(true);
                }, 3000);
            }
        };

        checkInstallPrompt();
    }, []);

    // Fetch latest analysis data using token
    useEffect(() => {
        const fetchLatestAnalysis = async () => {
            setLoading(true);
            setError(null);
            setFetchedData(null);

            const token = localStorage.getItem('firebaseIdToken');
            if (!token) {
                console.error("HomePage: No token found, cannot fetch analysis.");
                setError("لطفا برای مشاهده اطلاعات وارد شوید.");
                setLoading(false);
                navigate('/login');
                return;
            }

            try {
                const data = await workerFetch(`${WORKER_URL}/api/user/latest-analysis`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                console.log("HomePage: API response data:", data);

                if (data && data.id) {
                    console.log("HomePage: Latest analysis fetched successfully:", data);
                    setFetchedData(data);
                } else {
                    console.log("HomePage: No valid analysis found for user.");
                    setFetchedData(null);
                }
            } catch (error) {
                console.error("HomePage: Failed to fetch latest analysis:", error?.message || error);
                setError("خطا در دریافت اطلاعات آنالیز.");
            } finally {
                setLoading(false);
            }
        };

        // Fetch user profile
        const fetchUserProfile = async () => {
            const token = localStorage.getItem('firebaseIdToken');
            if (!token) return;

            try {
                const profile = await workerFetch(`${WORKER_URL}/api/user/profile`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                });

                if (profile) {
                    setUserProfile(profile);
                }
            } catch (error) {
                console.error("Error fetching user profile:", error);
            }
        };

        fetchLatestAnalysis();
        fetchUserProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Process analysis data
    const processedAnalysis = useMemo(() => {
        if (!fetchedData) return null;

        try {
            // API returns analysis as an object already
            const analysis = fetchedData.analysis || fetchedData;
            const score = extractScore(analysis);
            const explanation = extractText(analysis, topLevelExplanationKeys);

            console.log("✅ Processed analysis:", { score, explanation, id: fetchedData.id });

            return {
                score,
                explanation,
                analysisId: fetchedData.id,
                date: fetchedData.createdAt,
                rawAnalysis: analysis
            };
        } catch (error) {
            console.error("❌ Error processing analysis:", error);
            return null;
        }
    }, [fetchedData]);

    const handleNavigateToAnalysis = (analysisId) => {
        if (analysisId) {
            navigate(`/analysis/${analysisId}`);
        } else if (processedAnalysis?.analysisId) {
            navigate(`/analysis/${processedAnalysis.analysisId}`);
        }
    };

    return (
        <div className="min-h-screen" dir="rtl">
            <LoadingOverlay 
                isLoading={loading} 
                message="در حال بارگذاری اطلاعات..."
            />
            
            

            {/* Main Skin Profile */}
            <SkinProfile 
                userProfile={userProfile}
                processedAnalysis={processedAnalysis}
                onNavigateToAnalysis={handleNavigateToAnalysis}
            />

            {/* Onboarding Tour */}
            <OnboardingTour />

            {/* PWA Install Modal */}
            <PWAInstallModal 
                isOpen={showInstallModal}
                onClose={() => setShowInstallModal(false)}
            />
        </div>
    );
};

export default HomePage; 